import { Box, ButtonBase, Stack, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { memo } from 'react';
import { NodeProps } from 'reactflow';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { WorkflowAddStepMenu } from 'src/pages/Settings/workflows/components/workflow-add-step-menu';
import { WorkflowNodeType } from 'src/pages/Settings/workflows/utils/workflow-definition';
import { outputKey } from 'src/pages/Settings/workflows/utils/workflow-folding';
import { WorkflowNodeDisplayData } from 'src/pages/Settings/workflows/utils/workflow-helpers';
import { useWorkflowEditor } from 'src/pages/Settings/workflows/workflow-editor-context';
import { BaseNode } from './base-node';

const OUTPUTS = [
  { condition: true, fallbackLabel: 'True', handleId: 'if-true' },
  { condition: false, fallbackLabel: 'False', handleId: 'if-false' },
] as const;

type ChipTone = 'true' | 'false' | 'add' | 'empty';

/** An output's chip at the foot of the card, as in the editor prototype. */
const OutputChip = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: ChipTone }>(({ theme, tone }) => {
  const sys = theme.palette.custom.OV.sys;
  const green = theme.palette.mode === 'dark' ? '#66bb6a' : '#2e7d32';
  const color = {
    true: green,
    false: sys.onSurfaceVariant,
    add: theme.palette.secondary.main,
    empty: sys.onSurfaceVariant,
  }[tone];

  let background = 'transparent';
  if (tone === 'true') background = alpha(green, 0.14);
  else if (tone === 'false') background = sys.surfaceContainer;

  return {
    height: 22,
    maxWidth: '100%',
    gap: theme.spacing(0.5),
    paddingInline: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    color,
    border: `1px ${tone === 'add' || tone === 'empty' ? 'dashed' : 'solid'} ${
      tone === 'false' || tone === 'empty' ? sys.outlineVariant : alpha(color, 0.6)
    }`,
    backgroundColor: background,
    opacity: tone === 'empty' ? 0.6 : 1,
    '&:hover': tone === 'empty' ? {} : { backgroundColor: alpha(color, 0.12) },
  };
});

const FoldCount = styled('span')(({ theme }) => ({
  marginLeft: theme.spacing(0.25),
  paddingInline: 4,
  borderRadius: 4,
  fontSize: '0.62rem',
  backgroundColor: 'currentColor',
  '& > span': { color: theme.palette.custom.OV.sys.surfaceContainerLowest },
}));

/**
 * The outputs of an `if`, one chip each under its connection: pressing one folds or opens the
 * steps it leads to, with a count while folded. An empty output in a draft is "+ True" or
 * "+ False", which adds a step there; left empty, a run that answers that way ends at the if.
 */
export const ConditionOutputs = ({ nodeId }: { nodeId: string }) => {
  const { definition, catalog, editable, view } = useWorkflowEditor();
  const stepType = catalog.byType.get(
    definition.nodes.find(({ id }) => id === nodeId)?.type ?? '',
  );

  return (
    <Stack direction="row" className="nodrag nopan" sx={{ mt: 1 }}>
      {OUTPUTS.map(({ condition, fallbackLabel, handleId }) => {
        const label =
          stepType?.outputs.find((output) => output.condition === condition)?.label ??
          fallbackLabel;
        const key = outputKey(nodeId, condition);
        const hasStep = definition.edges.some(
          ({ source, data }) => source === nodeId && data?.condition === condition,
        );

        let chip;
        if (hasStep) {
          const folded = view.folded.has(key);
          const hidden = folded ? (view.hiddenCounts.get(key) ?? 0) : 0;
          chip = (
            <OutputChip
              tone={condition ? 'true' : 'false'}
              aria-pressed={folded}
              aria-label={`${label}: ${folded ? 'show' : 'fold'} the steps after it`}
              title={`${label}: ${folded ? 'show' : 'fold'} the steps after it`}
              onClick={(event) => {
                event.stopPropagation();
                view.toggleFold(key);
              }}
            >
              {label}
              {folded ? (
                <FoldCount>
                  <span>+{hidden}</span>
                </FoldCount>
              ) : null}
            </OutputChip>
          );
        } else if (editable) {
          chip = (
            <WorkflowAddStepMenu
              insertion={{ kind: 'output', sourceId: nodeId, condition }}
              renderTrigger={({ getTriggerProps }) => (
                <OutputChip
                  tone="add"
                  {...getTriggerProps()}
                  aria-label={`Add a step on ${label}`}
                >
                  <DynamicIcon name="add" sx={{ fontSize: 13 }} />
                  {label}
                </OutputChip>
              )}
            />
          );
        } else {
          chip = (
            <OutputChip tone="empty" disabled title={`Nothing runs on ${label}`}>
              {label}
            </OutputChip>
          );
        }

        return (
          <Box key={handleId} sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            {chip}
          </Box>
        );
      })}
    </Stack>
  );
};

/**
 * The chip at the foot of a step with one output and something after it: pressing it folds the
 * steps that follow, with a count while folded. The trigger has none, so the first step always
 * shows.
 */
export const NextStepsOutput = ({ nodeId }: { nodeId: string }) => {
  const { definition, view } = useWorkflowEditor();
  const node = definition.nodes.find(({ id }) => id === nodeId);
  const hasNext = definition.edges.some(({ source }) => source === nodeId);
  if (!node || node.type === WorkflowNodeType.Trigger || !hasNext) return null;

  const key = outputKey(nodeId);
  const folded = view.folded.has(key);
  const hidden = folded ? (view.hiddenCounts.get(key) ?? 0) : 0;
  const label = `${folded ? 'Show' : 'Fold'} the steps after this one`;

  return (
    <Stack direction="row" justifyContent="center" className="nodrag nopan" sx={{ mt: 1 }}>
      <OutputChip
        tone="false"
        aria-pressed={folded}
        aria-label={label}
        title={label}
        onClick={(event) => {
          event.stopPropagation();
          view.toggleFold(key);
        }}
      >
        <DynamicIcon name={folded ? 'unfold_more' : 'unfold_less'} sx={{ fontSize: 13 }} />
        Next
        {folded ? (
          <FoldCount>
            <span>+{hidden}</span>
          </FoldCount>
        ) : null}
      </OutputChip>
    </Stack>
  );
};

const ConditionalNodeComponent = (props: NodeProps<WorkflowNodeDisplayData>) => (
  <BaseNode
    {...props}
    sourceHandles={[
      { id: 'if-true', offset: '25%' },
      { id: 'if-false', offset: '75%' },
    ]}
    footer={<ConditionOutputs nodeId={props.data.nodeId} />}
  />
);

ConditionalNodeComponent.displayName = 'ConditionalNode';

export const ConditionalNode = memo(ConditionalNodeComponent);
